/* TDB Recipe Editor · Phase 1 UI (per Dipali · 20-May-2026)
 *
 * Three sections (Bases / Flavours / Decorations) · each lists every
 * template recipe in that kind · click into one to see its ingredient
 * lines · add/edit/delete lines · view dependent SKUs · view version
 * history (Phase G).
 *
 * Rates are read-only · they come from the Ingredient Master · clicking
 * the rate cell opens a link to that ingredient in the master.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ChefHat, ChevronLeft, ChevronRight, Loader2, Plus, X, Trash2, Edit3,
  History, Beaker, AlertTriangle, Eye, Lock, CheckCircle2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';

const KIND_LABEL = {
  base:       { label: 'Bases',       icon: '🍞', color: 'amber' },
  flavour:    { label: 'Flavours',    icon: '🍗', color: 'emerald' },
  decoration: { label: 'Decorations', icon: '🎀', color: 'pink' },
};

const fmtRupees = (n) => `₹${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};


export default function TDBRecipeEditor({ currentUser }) {
  const [recipes, setRecipes] = useState({ base: [], flavour: [], decoration: [] });
  const [loading, setLoading] = useState(true);
  const [openKind, setOpenKind] = useState('base');
  const [openRecipe, setOpenRecipe] = useState(null);  // {kind, name}
  const navigate = useNavigate();

  const email = (currentUser?.email || '').toLowerCase();
  const isCEO = currentUser?.role === 'ceo' || email === 'dipali@clubconcierge.in';
  const isRoji = email === 'roji.mathew@mindescapes.in';
  const isAditya = email === 'sikand.adi@gmail.com';
  const canEdit = isCEO || isRoji || isAditya;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/tdb-mis/recipes');
      setRecipes({
        base: r.data.base || [],
        flavour: r.data.flavour || [],
        decoration: r.data.decoration || [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div data-testid="tdb-recipe-editor-loading" className="bg-white border rounded-2xl p-8 text-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto mb-2" />
        <span className="text-sm text-slate-500">Loading recipes…</span>
      </div>
    );
  }

  return (
    <div data-testid="tdb-recipe-editor" className="space-y-4">
      <div className="bg-white border rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ChefHat className="w-5 h-5 text-amber-700" />
              <h2 className="text-base font-semibold text-slate-900">
                Recipe Editor · Phase 1
              </h2>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium uppercase tracking-wider">
                v2 · Roji approved
              </span>
            </div>
            <p className="text-xs text-slate-500">
              Edit base / flavour / decoration recipes here.
              {' '}<span className="font-medium">Rates are read-only</span> — they come from the Ingredient Master.
              {' '}Every save cascades to the dependent SKUs.
            </p>
          </div>
          <button
            data-testid="tdb-recipe-open-im"
            onClick={() => navigate('/tdb/ingredient-master')}
            className="text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 flex items-center gap-1"
          >
            <Beaker className="w-3.5 h-3.5" /> Ingredient Master
          </button>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
          {(['base', 'flavour', 'decoration']).map((k) => (
            <button
              key={k}
              data-testid={`tdb-recipe-kind-${k}`}
              onClick={() => setOpenKind(k)}
              className={`px-3 py-2 rounded-md border text-left transition ${
                openKind === k
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white hover:bg-slate-50'
              }`}
            >
              <span className="text-base mr-1">{KIND_LABEL[k].icon}</span>
              <span className="font-medium">{KIND_LABEL[k].label}</span>
              <span className={`ml-2 text-[10px] ${openKind === k ? 'opacity-80' : 'opacity-60'}`}>
                {recipes[k].length} recipes
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600 tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Recipe</th>
              <th className="text-right px-3 py-2">Lines</th>
              <th className="text-right px-3 py-2">Total cost</th>
              <th className="text-right px-3 py-2">Used by SKUs</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {recipes[openKind].map((r) => (
              <tr
                key={r.name}
                data-testid={`tdb-recipe-row-${r.name.replace(/[^a-z0-9]/gi, '_')}`}
                className="border-t border-slate-100"
              >
                <td className="px-3 py-2 font-medium text-slate-900">{r.name}</td>
                <td className="px-3 py-2 text-right text-slate-700">{r.lines_count}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtRupees(r.total_cost)}</td>
                <td className="px-3 py-2 text-right">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.sku_count > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
                    {r.sku_count} SKU{r.sku_count !== 1 ? 's' : ''}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    data-testid={`tdb-recipe-open-${r.name.replace(/[^a-z0-9]/gi, '_')}`}
                    onClick={() => setOpenRecipe({ kind: openKind, name: r.name })}
                    className="text-xs px-2 py-1 rounded-md border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1"
                  >
                    {canEdit ? <Edit3 className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    {canEdit ? 'Edit' : 'View'} <ChevronRight className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
            {recipes[openKind].length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-sm text-slate-500">
                No {openKind} recipes
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {openRecipe && (
        <RecipeDetailModal
          kind={openRecipe.kind}
          name={openRecipe.name}
          canEdit={canEdit}
          onClose={() => { setOpenRecipe(null); load(); }}
        />
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Recipe detail · ingredient lines · edit · delete · versions
// ────────────────────────────────────────────────────────────────
function RecipeDetailModal({ kind, name, canEdit, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [ingredientsMaster, setIngredientsMaster] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tdb-mis/recipes/${kind}/${encodeURIComponent(name)}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recipe');
    } finally {
      setLoading(false);
    }
  }, [kind, name]);

  const loadIngredientsMaster = useCallback(async () => {
    try {
      const r = await api.get('/tdb-mis/ingredient-master');
      setIngredientsMaster((r.data?.ingredients || []).filter((i) => i.active));
    } catch (e) {
      setIngredientsMaster([]);
    }
  }, []);

  const loadVersions = useCallback(async () => {
    try {
      const r = await api.get(`/tdb-mis/recipes/${kind}/versions/${encodeURIComponent(name)}`);
      setVersions(r.data?.versions || []);
    } catch (e) {
      setVersions([]);
    }
  }, [kind, name]);

  useEffect(() => { load(); loadIngredientsMaster(); }, [load, loadIngredientsMaster]);
  useEffect(() => { if (showVersions) loadVersions(); }, [showVersions, loadVersions]);

  const deleteLine = async (ingredient) => {
    const reason = window.prompt(
      `Remove "${ingredient}" from "${name}"?\n\nReason (e.g. "moved to a different recipe"):`,
    );
    if (reason === null) return;
    setBusyKey(`delete|${ingredient}`);
    try {
      const r = await api.delete(
        `/tdb-mis/recipes/${kind}/${encodeURIComponent(name)}/line/${encodeURIComponent(ingredient)}`,
        { data: { reason } },
      );
      const n = r.data?.cascade?.skus_recosted || 0;
      toast.success(`Removed · ${n} SKU${n !== 1 ? 's' : ''} recosted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyKey('');
    }
  };

  const saveLineEdit = async (line, newQty, newUnit, reason) => {
    setBusyKey(`edit|${line.ingredient}`);
    try {
      const r = await api.put(
        `/tdb-mis/recipes/${kind}/${encodeURIComponent(name)}/line`,
        {
          ingredient: line.ingredient,
          quantity: newQty,
          unit: newUnit,
          reason,
        },
      );
      const n = r.data?.cascade?.skus_recosted || 0;
      toast.success(`Saved · ${n} SKU${n !== 1 ? 's' : ''} recosted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div
      data-testid="tdb-recipe-detail-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl border">
        <div className="flex items-start justify-between p-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl">{KIND_LABEL[kind].icon}</span>
              <h3 className="text-base font-semibold text-slate-900">{name}</h3>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 uppercase tracking-wider">
                {kind}
              </span>
            </div>
            {detail && (
              <div className="text-xs text-slate-500 mt-1">
                {detail.lines_count} ingredient lines · total <span className="font-mono">{fmtRupees(detail.total_cost)}</span>
                {' · '}{detail.dependent_count} SKU{detail.dependent_count !== 1 ? 's' : ''} affected
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="tdb-recipe-history-btn"
              onClick={() => setShowVersions(!showVersions)}
              className={`text-xs px-2 py-1.5 rounded-md flex items-center gap-1 ${
                showVersions ? 'bg-slate-900 text-white' : 'border border-slate-300 hover:bg-slate-50'
              }`}
            >
              <History className="w-3 h-3" /> History
            </button>
            <button
              data-testid="tdb-recipe-detail-close"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-900 ml-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading || !detail ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading…
          </div>
        ) : (
          <>
            <div className="p-4 space-y-4">
              {/* Ingredient lines table */}
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Ingredient</th>
                      <th className="text-right px-3 py-2">Qty</th>
                      <th className="text-left px-3 py-2">Unit</th>
                      <th className="text-right px-3 py-2">Rate (from master)</th>
                      <th className="text-right px-3 py-2">Line cost</th>
                      <th className="text-right px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.lines.map((line, i) => (
                      <LineRow
                        key={line.ingredient}
                        index={i + 1}
                        line={line}
                        canEdit={canEdit}
                        busy={busyKey.startsWith(`edit|${line.ingredient}`) || busyKey.startsWith(`delete|${line.ingredient}`)}
                        onSave={(qty, unit, reason) => saveLineEdit(line, qty, unit, reason)}
                        onDelete={() => deleteLine(line.ingredient)}
                      />
                    ))}
                    {detail.lines.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-8 text-sm text-slate-500">
                        No ingredients yet · click "Add ingredient" below
                      </td></tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 border-t-2 border-slate-200 text-xs font-medium">
                      <td colSpan={5} className="text-right px-3 py-2 uppercase tracking-wider text-slate-600">
                        Total recipe cost
                      </td>
                      <td className="text-right px-3 py-2 font-mono font-semibold text-base">
                        {fmtRupees(detail.total_cost)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {canEdit && (
                showAdd ? (
                  <AddLineRow
                    kind={kind} name={name}
                    ingredientsMaster={ingredientsMaster}
                    onCancel={() => setShowAdd(false)}
                    onSaved={() => { setShowAdd(false); load(); }}
                  />
                ) : (
                  <button
                    data-testid="tdb-recipe-add-line"
                    onClick={() => setShowAdd(true)}
                    className="w-full text-sm px-3 py-2 rounded-md border-2 border-dashed border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add ingredient
                  </button>
                )
              )}

              {showVersions && (
                <RecipeVersions versions={versions} />
              )}

              {/* Dependent SKUs */}
              <DependentSKUs skus={detail.dependent_skus} count={detail.dependent_count} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// One ingredient row · in-place edit
// ────────────────────────────────────────────────────────────────
function LineRow({ index, line, canEdit, busy, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [qty, setQty] = useState(String(line.quantity));
  const [unit, setUnit] = useState(line.unit);
  const [reason, setReason] = useState('');

  const save = () => {
    const n = parseFloat(qty);
    if (!Number.isFinite(n) || n < 0) {
      toast.error('Qty must be a non-negative number');
      return;
    }
    if (!reason.trim()) {
      toast.error('Reason mandatory for any edit');
      return;
    }
    onSave(n, unit, reason.trim());
    setEditing(false);
    setReason('');
  };

  if (!editing) {
    return (
      <tr
        data-testid={`tdb-recipe-line-${line.ingredient.replace(/[^a-z0-9]/gi, '_')}`}
        className="border-t border-slate-100 hover:bg-slate-50/50"
      >
        <td className="px-3 py-2 text-xs text-slate-400 font-mono">{index}</td>
        <td className="px-3 py-2 font-medium text-slate-900">{line.ingredient}</td>
        <td className="px-3 py-2 text-right font-mono">{Number(line.quantity).toLocaleString('en-IN', { maximumFractionDigits: 3 })}</td>
        <td className="px-3 py-2 text-slate-600 text-xs">{line.unit}</td>
        <td className="px-3 py-2 text-right font-mono text-xs">
          {fmtRupees(line.rate)}
          {line.rate_source === 'master' && (
            <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-emerald-700">
              <Lock className="w-2.5 h-2.5" /> master
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-right font-mono">{fmtRupees(line.line_cost)}</td>
        <td className="px-3 py-2 text-right">
          {canEdit && (
            <div className="flex items-center justify-end gap-1">
              <button
                data-testid={`tdb-recipe-edit-line-${line.ingredient.replace(/[^a-z0-9]/gi, '_')}`}
                onClick={() => setEditing(true)}
                disabled={busy}
                className="text-xs px-1.5 py-0.5 rounded border border-slate-300 hover:bg-slate-100"
              >
                <Edit3 className="w-3 h-3" />
              </button>
              <button
                data-testid={`tdb-recipe-delete-line-${line.ingredient.replace(/[^a-z0-9]/gi, '_')}`}
                onClick={onDelete}
                disabled={busy}
                className="text-xs px-1.5 py-0.5 rounded border border-red-200 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          )}
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-slate-100 bg-amber-50/40">
      <td className="px-3 py-2 text-xs text-slate-400 font-mono">{index}</td>
      <td className="px-3 py-2 font-medium text-slate-900">{line.ingredient}</td>
      <td className="px-3 py-2 text-right">
        <input
          type="number" step="0.001"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="w-20 text-right text-sm border rounded px-1 py-0.5"
        />
      </td>
      <td className="px-3 py-2">
        <select value={unit} onChange={(e) => setUnit(e.target.value)} className="text-xs border rounded px-1 py-0.5">
          {['grams', 'piece', 'kg', 'litre', 'ml'].map((u) => <option key={u}>{u}</option>)}
        </select>
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">{fmtRupees(line.rate)}</td>
      <td colSpan={2} className="px-3 py-2 text-right">
        <input
          type="text"
          placeholder="reason for change (mandatory)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-48 text-xs border rounded px-2 py-0.5 mr-1"
        />
        <button onClick={save} disabled={busy || !reason.trim()} className="text-xs px-2 py-0.5 rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
        </button>
        <button onClick={() => { setEditing(false); setReason(''); setQty(String(line.quantity)); }} className="text-xs px-2 py-0.5 rounded border ml-1">
          Cancel
        </button>
      </td>
    </tr>
  );
}


// ────────────────────────────────────────────────────────────────
// Inline "Add ingredient" row
// ────────────────────────────────────────────────────────────────
function AddLineRow({ kind, name, ingredientsMaster, onSaved, onCancel }) {
  const [ingredient, setIngredient] = useState('');
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('grams');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Auto-default unit from master pick
  useEffect(() => {
    const m = ingredientsMaster.find((i) => i.item_name === ingredient);
    if (m && m.unit) setUnit(m.unit);
  }, [ingredient, ingredientsMaster]);

  const save = async () => {
    if (!ingredient) { toast.error('Pick an ingredient from the master'); return; }
    const n = parseFloat(qty);
    if (!Number.isFinite(n) || n <= 0) { toast.error('Qty must be > 0'); return; }
    if (!reason.trim()) { toast.error('Reason mandatory'); return; }
    setBusy(true);
    try {
      await api.put(
        `/tdb-mis/recipes/${kind}/${encodeURIComponent(name)}/line`,
        { ingredient, quantity: n, unit, reason: reason.trim() },
      );
      toast.success('Ingredient added · cascade fired');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="tdb-recipe-add-line-row" className="rounded-md border-2 border-dashed border-amber-400 bg-amber-50/40 p-3">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
        <div className="md:col-span-4">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Ingredient</label>
          <select
            data-testid="tdb-recipe-add-line-ingredient"
            value={ingredient}
            onChange={(e) => setIngredient(e.target.value)}
            className="w-full text-sm border rounded px-2 py-1 mt-0.5"
          >
            <option value="">Pick from master ({ingredientsMaster.length})…</option>
            {ingredientsMaster.map((i) => (
              <option key={i.item_name} value={i.item_name}>
                {i.item_name} · {i.category} · {fmtRupees(i.rate)}/{i.unit}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Quantity</label>
          <input
            data-testid="tdb-recipe-add-line-qty"
            type="number" step="0.001" value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="w-full text-sm border rounded px-2 py-1 mt-0.5 text-right"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Unit</label>
          <select
            data-testid="tdb-recipe-add-line-unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="w-full text-sm border rounded px-2 py-1 mt-0.5"
          >
            {['grams', 'piece', 'kg', 'litre', 'ml'].map((u) => <option key={u}>{u}</option>)}
          </select>
        </div>
        <div className="md:col-span-4">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Reason (audit log)</label>
          <input
            data-testid="tdb-recipe-add-line-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. recipe tweak for richer texture"
            maxLength={500}
            className="w-full text-sm border rounded px-2 py-1 mt-0.5"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onCancel} disabled={busy} className="text-xs px-3 py-1 rounded border border-slate-300">Cancel</button>
        <button
          data-testid="tdb-recipe-add-line-save"
          onClick={save}
          disabled={busy || !ingredient || !qty || !reason.trim()}
          className="text-xs px-4 py-1 rounded bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50 flex items-center gap-1"
        >
          {busy && <Loader2 className="w-3 h-3 animate-spin" />}
          Add & cascade
        </button>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Version history timeline
// ────────────────────────────────────────────────────────────────
function RecipeVersions({ versions }) {
  if (!versions.length) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-500">
        <History className="w-4 h-4 mx-auto mb-1" />
        No edits yet · this is the first version
      </div>
    );
  }
  return (
    <div
      data-testid="tdb-recipe-versions"
      className="rounded-md border border-slate-200 bg-slate-50/50 p-3"
    >
      <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
        <History className="w-3 h-3" />
        Edit history · {versions.length} snapshot{versions.length === 1 ? '' : 's'}
      </div>
      <div className="space-y-2">
        {versions.map((v, i) => (
          <div
            key={i}
            data-testid={`tdb-recipe-version-${i}`}
            className="rounded-md bg-white border border-slate-200 p-2"
          >
            <div className="flex items-center justify-between gap-2 text-[11px]">
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-slate-500">{fmtDate(v.snapshot_at)}</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 uppercase tracking-wider">{v.trigger}</span>
                <span className="text-slate-500">·</span>
                <span className="font-medium text-slate-700">{(v.changed_by || '').split('@')[0]}</span>
              </div>
              <span className="text-slate-500">{v.lines_count} lines</span>
            </div>
            {v.reason && (
              <div className="text-[11px] text-slate-600 mt-1">
                <span className="text-slate-500 uppercase tracking-wider text-[9px]">Reason · </span>
                {v.reason}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Dependent SKUs preview
// ────────────────────────────────────────────────────────────────
function DependentSKUs({ skus, count }) {
  const [open, setOpen] = useState(false);
  if (!count) return null;
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-emerald-900 flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {count} SKU{count !== 1 ? 's' : ''} use this recipe · edits cascade live
        </span>
        <button
          data-testid="tdb-recipe-toggle-skus"
          onClick={() => setOpen(!open)}
          className="text-xs text-emerald-800 underline hover:text-emerald-900"
        >
          {open ? 'Hide' : 'View list'}
        </button>
      </div>
      {open && (
        <div className="mt-2 max-h-60 overflow-y-auto rounded border border-emerald-200 bg-white">
          <table className="w-full text-[11px]">
            <thead className="bg-emerald-100/60 text-emerald-900 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-2 py-1">Product</th>
                <th className="text-left px-2 py-1">Variant</th>
                <th className="text-right px-2 py-1">Food ₹</th>
                <th className="text-right px-2 py-1">Margin</th>
              </tr>
            </thead>
            <tbody>
              {skus.slice(0, 100).map((s, i) => (
                <tr key={s.shopify_variant_id || i} className="border-t border-emerald-100">
                  <td className="px-2 py-1">{s.product_title}</td>
                  <td className="px-2 py-1 text-slate-600">{s.variant_title}</td>
                  <td className="px-2 py-1 text-right font-mono">{fmtRupees(s.food_cost)}</td>
                  <td className="px-2 py-1 text-right">{Number(s.margin_pct || 0).toFixed(1)}%</td>
                </tr>
              ))}
              {skus.length > 100 && (
                <tr><td colSpan={4} className="text-center text-[10px] py-1 text-slate-500">
                  …and {skus.length - 100} more
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
